/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import axios from "axios";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { CACHE_30_MINS } from "../../utils/cache-utils";
import { InitiatePaymentResponse, SubscriptionPlan } from "./types";

const API = axios.create({
    baseURL: `${
        import.meta.env.VITE_APP_WEB_ENDPOINT ||
        "https://admin-develop.habitnu.com"
    }/api`,
    params: {
        uuid: sessionStorage.getItem("uuid"),
        timezone: sessionStorage.getItem("timezone"),
    },
    headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
    },
});

API.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response.data.error === "User not found") {
            sessionStorage.clear();
            window.location.href = "/";
        }
        return Promise.reject(new Error((error.response?.statusText as string) || 'Request failed'));
    }
);

const updateAxiosInterceptors = () => {
    API.interceptors.request.use(
        (config: import("axios").AxiosRequestConfig) => {
            const params: Record<string, unknown> = {
                ...((config.params as Record<string, unknown>) ?? {}),
                uuid: sessionStorage.getItem("uuid") || "",
                timezone: sessionStorage.getItem("timezone") || "",
            };
            return {
                ...config,
                params,
            };
        }
    );

    return API;
};

export const useSubscriptionPlans = (uuid: string, timezone: string) =>
    useQuery(
        ["subscription", "plans"],
        () =>
            updateAxiosInterceptors()
                .get<SubscriptionPlan[]>("/subscriptions/plans", {
                    params: {
                        uuid,
                        timezone,
                    },
                })
                .then(({ data }) => data),
        {
            refetchOnWindowFocus: false,
            staleTime: CACHE_30_MINS,
        }
    );

export const useInitiatePayment = () =>
    useMutation(
        async (args: {
            productId: string;
            provider: string;
            options?: Record<string, unknown>;
        }) => {
            const { productId, provider, options } = args;
            await updateAxiosInterceptors()
                .post<InitiatePaymentResponse>("/payments/initiate", {
                    product_id: productId,
                    provider,
                    options: options || {},
                })
                .then((response) => {
                    const {
                        checkout_url: checkoutUrl,
                        message,
                        success,
                    } = response.data;
                    if (success && checkoutUrl) {
                        window.location.href = checkoutUrl;
                    } else {
                        toast.warning(
                            message ||
                                "unable to initiate payment, try again later."
                        );
                    }
                })
                .catch(({ data }: { data: { message: string } }) => {
                    const errorMessage =
                        data?.message || "Payment initiation failed";
                    toast.error(errorMessage);
                });
        }
    );

export function useVerifyPayment(onSuccess?: () => void) {
    return useMutation(
        async (args: { sessionId: string; productId: string }) => {
            interface VerifyPaymentResponse {
                success: boolean;
                error?: string;
            }
            const response =
                await updateAxiosInterceptors().post<VerifyPaymentResponse>(
                    `/payments/verify`,
                    {
                        session_id: args.sessionId,
                        product_id: args.productId,
                        provider: "stripe",
                    }
                );
            return response.data;
        },
        {
            onSuccess: (data) => {
                if (data.success && onSuccess) {
                    onSuccess();
                }
            },
        }
    );
}
